import React, { useState } from 'react';
import { QueryBuilder, RuleGroupType, Field } from 'react-querybuilder';
import 'react-querybuilder/dist/query-builder.css';
import { Filter, Trigger, Notification, TriggerCategory } from '../types';
import { convertQueryToJsonLogic } from '../utils/jsonLogicConverter';

interface RuleBuilderProps {
  filters: Filter[];
  triggers: Trigger[];
  initialFilters?: RuleGroupType;
  initialTriggers?: RuleGroupType;
  initialNotification?: Notification;
  onSave: (filters: RuleGroupType, triggers: RuleGroupType, notification: Notification) => void;
  onCancel: () => void;
}

const RuleBuilder: React.FC<RuleBuilderProps> = ({
  filters,
  triggers,
  initialFilters,
  initialTriggers,
  initialNotification,
  onSave,
  onCancel
}) => {
  const [filterQuery, setFilterQuery] = useState<RuleGroupType>(
    initialFilters || { combinator: 'and', rules: [] }
  );
  const [triggerQuery, setTriggerQuery] = useState<RuleGroupType>(
    initialTriggers || { combinator: 'and', rules: [] }
  );
  const [notification, setNotification] = useState<Notification>(
    initialNotification || {
      id: '',
      topic: '',
      body: '',
      priority: 'medium',
      channels: ['push']
    }
  );
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  const filterFields: Field[] = filters.map(filter => ({
    name: filter.field,
    label: filter.label,
    valueEditorType: filter.type === 'number' ? 'text' : (filter.type === 'date' ? 'text' : filter.type as any),
    values: filter.options,
    defaultOperator: filter.type === 'select' ? '=' : '>='
  }));

  const triggerFields: Field[] = triggers.map(trigger => ({
    name: trigger.field,
    label: `${trigger.label}${trigger.unit ? ` (${trigger.unit})` : ''}`,
    valueEditorType: trigger.type === 'number' ? 'text' : (trigger.type === 'date' || trigger.type === 'boolean' ? 'text' : trigger.type as any),
    values: trigger.options?.map(opt => ({ ...opt, value: String(opt.value) })),
    defaultOperator: trigger.type === 'select' ? '=' : '>='
  }));

  const groupedTriggers = triggers.reduce((acc, trigger) => {
    if (!acc[trigger.category]) {
      acc[trigger.category] = [];
    }
    acc[trigger.category].push(trigger);
    return acc;
  }, {} as Record<TriggerCategory, Trigger[]>);

  const handleSave = () => {
    if (!notification.topic || !notification.body) {
      alert('Please fill in notification topic and body');
      return;
    }
    onSave(filterQuery, triggerQuery, { ...notification, id: notification.id || Date.now().toString() });
  };

  const getJsonLogicPreview = () => {
    return {
      filters: convertQueryToJsonLogic(filterQuery),
      triggers: convertQueryToJsonLogic(triggerQuery),
      notification
    };
  };

  return (
    <div className="rule-builder">
      <h2>Rule Builder</h2>

      <div className="section">
        <h3>Step 1: Define Filters (Optional)</h3>
        <p className="helper-text">Filter which dogs this rule applies to</p>
        <QueryBuilder
          fields={filterFields}
          query={filterQuery}
          onQueryChange={setFilterQuery}
        />
      </div>

      <div className="section">
        <h3>Step 2: Define Triggers</h3>
        <p className="helper-text">When should this rule trigger?</p>
        
        <div className="trigger-categories">
          <div className="category-pills">
            {Object.entries(groupedTriggers).map(([category, categoryTriggers]) => (
              <div key={category} className="category-section">
                <h4>{category.charAt(0).toUpperCase() + category.slice(1)} Triggers</h4>
                <div className="trigger-list">
                  {categoryTriggers.map(trigger => (
                    <span key={trigger.id} className="trigger-pill">
                      {trigger.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <QueryBuilder
          fields={triggerFields}
          query={triggerQuery}
          onQueryChange={setTriggerQuery}
        />
      </div>

      <div className="section">
        <h3>Step 3: Configure Notification</h3>
        <div className="notification-form">
          <div className="form-group">
            <label>Topic/Title *</label>
            <input
              type="text"
              value={notification.topic}
              onChange={(e) => setNotification({ ...notification, topic: e.target.value })}
              placeholder="e.g., Uwaga na upał"
            />
          </div>

          <div className="form-group">
            <label>Message Body *</label>
            <textarea
              value={notification.body}
              onChange={(e) => setNotification({ ...notification, body: e.target.value })}
              placeholder="e.g., Twój pies łatwo się przegrzewa. Unikaj spacerów w południe i zapewnij dostęp do wody."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Priority</label>
            <select
              value={notification.priority}
              onChange={(e) => setNotification({ ...notification, priority: e.target.value as 'low' | 'medium' | 'high' })}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-group">
            <label>Channels</label>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={notification.channels?.includes('push')}
                  onChange={(e) => {
                    const channels = notification.channels || [];
                    if (e.target.checked) {
                      setNotification({ ...notification, channels: [...channels, 'push'] });
                    } else {
                      setNotification({ ...notification, channels: channels.filter(c => c !== 'push') });
                    }
                  }}
                />
                Push Notification
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notification.channels?.includes('email')}
                  onChange={(e) => {
                    const channels = notification.channels || [];
                    if (e.target.checked) {
                      setNotification({ ...notification, channels: [...channels, 'email'] });
                    } else {
                      setNotification({ ...notification, channels: channels.filter(c => c !== 'email') });
                    }
                  }}
                />
                Email
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={notification.channels?.includes('sms')}
                  onChange={(e) => {
                    const channels = notification.channels || [];
                    if (e.target.checked) {
                      setNotification({ ...notification, channels: [...channels, 'sms'] });
                    } else {
                      setNotification({ ...notification, channels: channels.filter(c => c !== 'sms') });
                    }
                  }}
                />
                SMS
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="section">
        <button onClick={() => setShowJsonPreview(!showJsonPreview)} className="btn-secondary">
          {showJsonPreview ? 'Hide' : 'Show'} JSON Logic Preview
        </button>
        
        {showJsonPreview && (
          <div className="json-preview">
            <pre>{JSON.stringify(getJsonLogicPreview(), null, 2)}</pre>
          </div>
        )}
      </div>

      <div className="button-group">
        <button onClick={handleSave} className="btn-primary">Save Rule</button>
        <button onClick={onCancel} className="btn-secondary">Cancel</button>
      </div>
    </div>
  );
};

export default RuleBuilder;